import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import rrService from '../services/rrService';

// Stable key arrays — defined outside component so references never change
const INVEST_LEFT_KEYS = ['invest_cash_available'];
const INVEST_RIGHT_KEYS = ['invest_cash_per_group', 'invest_cash_per_account'];
const RAISE_LEFT_KEYS = ['raise_cash_needed'];
const RAISE_RIGHT_KEYS = ['raise_cash_per_group', 'raise_cash_per_account'];

// Static legend payloads — avoids recharts Legend lifecycle entirely
const RAISE_LEGEND_PAYLOAD = [
    { dataKey: 'raise_cash_needed',     value: 'Cash Needed',     color: '#dc3545', payload: { strokeDasharray: null } },
    { dataKey: 'raise_cash_per_group',  value: 'Avg per Group',   color: '#c82333', payload: { strokeDasharray: '8 4' } },
    { dataKey: 'raise_cash_per_account',value: 'Avg per Account', color: '#e57373', payload: { strokeDasharray: '2 4' } },
];
const INVEST_LEGEND_PAYLOAD = [
    { dataKey: 'invest_cash_available',  value: 'Cash Available',  color: '#28a745', payload: { strokeDasharray: null } },
    { dataKey: 'invest_cash_per_group',  value: 'Avg per Group',   color: '#20c997', payload: { strokeDasharray: '8 4' } },
    { dataKey: 'invest_cash_per_account',value: 'Avg per Account', color: '#17a2b8', payload: { strokeDasharray: '2 4' } },
];

/**
 * Format large dollar values for display
 */
const formatDollar = (value) => {
    if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
};

/**
 * Format date for display (MM/DD)
 */
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        return `${parts[1]}/${parts[2]}`;
    }
    return dateStr;
};

/**
 * Custom tooltip component for the charts
 */
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div style={{
                backgroundColor: 'white',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{label}</p>
                {payload.map((entry, index) => (
                    <p key={index} style={{ margin: '2px 0', color: entry.color }}>
                        {entry.name}: {entry.name.includes('Count')
                            ? entry.value.toFixed(1)
                            : formatDollar(entry.value)}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

/**
 * Custom legend component with clickable items
 * Shows dashed lines in legend matching the actual chart line styles
 */
const CustomLegend = ({ payload, hiddenLines, onToggleLine }) => {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            marginTop: '8px'
        }}>
            {payload.map((entry, index) => {
                const isHidden = hiddenLines.has(entry.dataKey);
                // Get the strokeDasharray from the payload (recharts passes this through)
                const strokeDasharray = entry.payload?.strokeDasharray || null;
                
                return (
                    <div
                        key={index}
                        onClick={() => onToggleLine(entry.dataKey)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            opacity: isHidden ? 0.4 : 1,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: isHidden ? '#f0f0f0' : 'transparent',
                            transition: 'all 0.2s ease'
                        }}
                        title={isHidden ? 'Click to show' : 'Click to hide'}
                    >
                        {/* Use SVG to properly render dashed lines in legend */}
                        <svg width="24" height="10" style={{ marginRight: '6px' }}>
                            <line
                                x1="0"
                                y1="5"
                                x2="24"
                                y2="5"
                                stroke={entry.color}
                                strokeWidth="2"
                                strokeDasharray={strokeDasharray || '0'}
                            />
                        </svg>
                        <span style={{
                            fontSize: '12px',
                            color: isHidden ? '#999' : '#333',
                            textDecoration: isHidden ? 'line-through' : 'none'
                        }}>
                            {entry.value}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Format currency for display
 */
const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

/**
 * RebalancerHistoryCharts - Displays trend charts for rebalancer history
 */
const RebalancerHistoryCharts = ({ days = 30, stats = null }) => {
    const [historyData, setHistoryData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Track hidden lines for each chart
    const [hiddenInvestLines, setHiddenInvestLines] = useState(new Set());
    const [hiddenRaiseLines, setHiddenRaiseLines] = useState(new Set());

    useEffect(() => {
        const fetchHistory = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await rrService.getRebalancerHistory(days);
                setHistoryData(response.history || []);
            } catch (err) {
                console.error('Failed to fetch rebalancer history:', err);
                setError('Failed to load history data');
            } finally {
                setIsLoading(false);
            }
        };

        fetchHistory();
    }, [days]);

    // Toggle visibility of a line in invest chart
    const toggleInvestLine = useCallback((dataKey) => {
        setHiddenInvestLines(prev => {
            const newSet = new Set(prev);
            if (newSet.has(dataKey)) {
                newSet.delete(dataKey);
            } else {
                newSet.add(dataKey);
            }
            return newSet;
        });
    }, []);

    // Toggle visibility of a line in raise chart
    const toggleRaiseLine = useCallback((dataKey) => {
        setHiddenRaiseLines(prev => {
            const newSet = new Set(prev);
            if (newSet.has(dataKey)) {
                newSet.delete(dataKey);
            } else {
                newSet.add(dataKey);
            }
            return newSet;
        });
    }, []);

    // Calculate Y-axis domain based on visible lines
    const getYAxisDomain = useCallback((dataKeys, hiddenLines) => {
        const visibleKeys = dataKeys.filter(key => !hiddenLines.has(key));
        if (visibleKeys.length === 0) return [0, 'auto'];

        let min = Infinity;
        let max = -Infinity;

        historyData.forEach(item => {
            visibleKeys.forEach(key => {
                const value = item[key];
                if (value !== undefined && value !== null) {
                    min = Math.min(min, value);
                    max = Math.max(max, value);
                }
            });
        });

        if (min === Infinity || max === -Infinity) return [0, 'auto'];

        // Add some padding (10%)
        const padding = (max - min) * 0.1;
        return [Math.max(0, min - padding), max + padding];
    }, [historyData]);

    // Memoize domain arrays so Recharts v3 doesn't see new array references each render
    const investLeftDomain = useMemo(() => getYAxisDomain(INVEST_LEFT_KEYS, hiddenInvestLines), [getYAxisDomain, hiddenInvestLines]);
    const investRightDomain = useMemo(() => getYAxisDomain(INVEST_RIGHT_KEYS, hiddenInvestLines), [getYAxisDomain, hiddenInvestLines]);
    const raiseLeftDomain = useMemo(() => getYAxisDomain(RAISE_LEFT_KEYS, hiddenRaiseLines), [getYAxisDomain, hiddenRaiseLines]);
    const raiseRightDomain = useMemo(() => getYAxisDomain(RAISE_RIGHT_KEYS, hiddenRaiseLines), [getYAxisDomain, hiddenRaiseLines]);


    if (isLoading) {
        return (
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading history...</span>
                            </div>
                            <p className="mt-2 mb-0">Loading history data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="row mb-4">
                <div className="col-12">
                    <div className="alert alert-warning mb-0">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </div>
                </div>
            </div>
        );
    }

    if (historyData.length === 0) {
        return (
            <div className="row mb-4">
                <div className="col-12">
                    <div className="alert alert-info mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        No history data available for the last {days} days.
                    </div>
                </div>
            </div>
        );
    }



    return (
        <div className="row mb-4 g-3">
            {/* Raise Cash Section */}
            {/* Raise Cash Stats Widget */}
            {stats && (
                <div className="col-md-2 mb-3">
                    <div className="card border-danger h-100">
                        <div className="card-header bg-danger text-white py-2">
                            <h6 className="mb-0 text-center">
                                <i className="bi bi-arrow-up-circle me-1"></i>
                                Raise Cash Today
                            </h6>
                        </div>
                        <div className="card-body d-flex flex-column justify-content-center py-3">
                            <div className="text-center mb-3">
                                <div className="text-muted small">Account Families</div>
                                <div className="h3 mb-0 text-danger">{stats.raiseCashGroups}</div>
                            </div>
                            <div className="text-center mb-3 border-top pt-3">
                                <div className="text-muted small">Trades</div>
                                <div className="h3 mb-0 text-danger">{stats.raiseCashTrades?.toLocaleString() || 0}</div>
                            </div>
                            <div className="text-center border-top pt-3">
                                <div className="text-muted small">Total Value</div>
                                <div className="h4 mb-0 text-danger">{formatCurrency(stats.raiseCashValue)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Raise Cash Chart */}
            <div className={stats ? "col-md-4 mb-3" : "col-md-6 mb-3"}>
                <div className="card h-100">
                    <div className="card-header bg-danger text-white">
                        <h6 className="mb-0">
                            <i className="bi bi-graph-down me-2"></i>
                            Raise Cash Trends
                            <small className="ms-2 opacity-75" style={{ fontSize: '0.75em' }}>
                                (click legend to toggle)
                            </small>
                        </h6>
                    </div>
                    <div className="card-body">
                        <CustomLegend payload={RAISE_LEGEND_PAYLOAD} hiddenLines={hiddenRaiseLines} onToggleLine={toggleRaiseLine} />
                        <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={historyData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={formatDate}
                                    tick={{ fontSize: 11 }}
                                />
                                <YAxis
                                    yAxisId="left"
                                    tickFormatter={formatDollar}
                                    tick={{ fontSize: 11, fill: '#dc3545' }}
                                    domain={raiseLeftDomain}
                                    label={{
                                        value: 'Total',
                                        angle: -90,
                                        position: 'insideLeft',
                                        style: { fontSize: 10, fill: '#dc3545' }
                                    }}
                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    tickFormatter={formatDollar}
                                    tick={{ fontSize: 11, fill: '#e57373' }}
                                    domain={raiseRightDomain}
                                    label={{
                                        value: 'Avg',
                                        angle: 90,
                                        position: 'insideRight',
                                        style: { fontSize: 10, fill: '#e57373' }
                                    }}
                                />
                                <Tooltip content={CustomTooltip} />
                                <Line
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="raise_cash_needed"
                                    name="Cash Needed"
                                    stroke="#dc3545"
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    activeDot={{ r: 5 }}
                                    isAnimationActive={false}
                                    hide={hiddenRaiseLines.has('raise_cash_needed')}
                                />
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="raise_cash_per_group"
                                    name="Avg per Group"
                                    stroke="#c82333"
                                    strokeWidth={2}
                                    strokeDasharray="8 4"
                                    dot={{ r: 2 }}
                                    isAnimationActive={false}
                                    hide={hiddenRaiseLines.has('raise_cash_per_group')}
                                />
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="raise_cash_per_account"
                                    name="Avg per Account"
                                    stroke="#e57373"
                                    strokeWidth={2}
                                    strokeDasharray="2 4"
                                    dot={{ r: 2 }}
                                    isAnimationActive={false}
                                    hide={hiddenRaiseLines.has('raise_cash_per_account')}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Invest Cash Section */}
            {/* Invest Cash Stats Widget */}
            {stats && (
                <div className="col-md-2 mb-3">
                    <div className="card border-success h-100">
                        <div className="card-header bg-success text-white py-2">
                            <h6 className="mb-0 text-center">
                                <i className="bi bi-arrow-down-circle me-1"></i>
                                Invest Cash Today
                            </h6>
                        </div>
                        <div className="card-body d-flex flex-column justify-content-center py-3">
                            <div className="text-center mb-3">
                                <div className="text-muted small">Account Families</div>
                                <div className="h3 mb-0 text-success">{stats.investCashGroups}</div>
                            </div>
                            <div className="text-center mb-3 border-top pt-3">
                                <div className="text-muted small">Trades</div>
                                <div className="h3 mb-0 text-success">{stats.investCashTrades?.toLocaleString() || 0}</div>
                            </div>
                            <div className="text-center border-top pt-3">
                                <div className="text-muted small">Total Value</div>
                                <div className="h4 mb-0 text-success">{formatCurrency(stats.investCashValue)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Invest Cash Chart */}
            <div className={stats ? "col-md-4 mb-3" : "col-md-6 mb-3"}>
                <div className="card h-100">
                    <div className="card-header bg-success text-white">
                        <h6 className="mb-0">
                            <i className="bi bi-graph-up me-2"></i>
                            Invest Cash Trends
                            <small className="ms-2 opacity-75" style={{ fontSize: '0.75em' }}>
                                (click legend to toggle)
                            </small>
                        </h6>
                    </div>
                    <div className="card-body">
                        <CustomLegend payload={INVEST_LEGEND_PAYLOAD} hiddenLines={hiddenInvestLines} onToggleLine={toggleInvestLine} />
                        <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={historyData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={formatDate}
                                    tick={{ fontSize: 11 }}
                                />
                                <YAxis
                                    yAxisId="left"
                                    tickFormatter={formatDollar}
                                    tick={{ fontSize: 11, fill: '#28a745' }}
                                    domain={investLeftDomain}
                                    label={{
                                        value: 'Total',
                                        angle: -90,
                                        position: 'insideLeft',
                                        style: { fontSize: 10, fill: '#28a745' }
                                    }}
                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    tickFormatter={formatDollar}
                                    tick={{ fontSize: 11, fill: '#17a2b8' }}
                                    domain={investRightDomain}
                                    label={{
                                        value: 'Avg',
                                        angle: 90,
                                        position: 'insideRight',
                                        style: { fontSize: 10, fill: '#17a2b8' }
                                    }}
                                />
                                <Tooltip content={CustomTooltip} />
                                <Line
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="invest_cash_available"
                                    name="Cash Available"
                                    stroke="#28a745"
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    activeDot={{ r: 5 }}
                                    isAnimationActive={false}
                                    hide={hiddenInvestLines.has('invest_cash_available')}
                                />
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="invest_cash_per_group"
                                    name="Avg per Group"
                                    stroke="#20c997"
                                    strokeWidth={2}
                                    strokeDasharray="8 4"
                                    dot={{ r: 2 }}
                                    isAnimationActive={false}
                                    hide={hiddenInvestLines.has('invest_cash_per_group')}
                                />
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="invest_cash_per_account"
                                    name="Avg per Account"
                                    stroke="#17a2b8"
                                    strokeWidth={2}
                                    strokeDasharray="2 4"
                                    dot={{ r: 2 }}
                                    isAnimationActive={false}
                                    hide={hiddenInvestLines.has('invest_cash_per_account')}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RebalancerHistoryCharts;
